import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  Activity, MessageSquare, Sparkles, CheckCircle, XCircle,
  Clock, Users, TrendingUp, Bot, Loader2, RefreshCw,
  BarChart3, PieChart as PieChartIcon, ArrowRight, Bell, AlertTriangle,
  AlertCircle, Info, TestTube
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color palette matching Zodiac brand
const COLORS = {
  primary: '#1E3A8A',
  secondary: '#991B1B',
  success: '#16A34A',
  warning: '#CA8A04',
  error: '#DC2626',
  chart: ['#1E3A8A', '#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#991B1B']
};

// Time range options
const TIME_RANGES = [
  { label: 'Last 24 Hours', hours: 24 },
  { label: 'Last 7 Days', hours: 168 },
  { label: 'Last 30 Days', hours: 720 }
];

// KPI Card Component
function KPICard({ title, value, icon: Icon, trend, color = 'primary', subtitle }) {
  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-3xl font-bold text-foreground mt-2">{value}</p>
          {subtitle && (
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
          )}
        </div>
        <div className={cn(
          "w-12 h-12 rounded-lg flex items-center justify-center",
          color === 'primary' && "bg-primary/10 text-primary",
          color === 'success' && "bg-green-100 text-green-600",
          color === 'warning' && "bg-yellow-100 text-yellow-600",
          color === 'error' && "bg-red-100 text-red-600"
        )}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
      {trend !== undefined && (
        <div className="mt-4 flex items-center gap-1">
          <TrendingUp className={cn(
            "w-4 h-4",
            trend >= 0 ? "text-green-600" : "text-red-600 rotate-180"
          )} />
          <span className={cn(
            "text-sm font-medium",
            trend >= 0 ? "text-green-600" : "text-red-600"
          )}>
            {Math.abs(trend)}%
          </span>
          <span className="text-xs text-muted-foreground ml-1">vs previous period</span>
        </div>
      )}
    </div>
  );
}

// Funnel Step Component
function FunnelStep({ step, count, rate, isLast, maxCount }) {
  const width = maxCount > 0 ? Math.max(30, (count / maxCount) * 100) : 30;
  
  return (
    <div className="flex items-center gap-4">
      <div className="flex-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">{step}</span>
          <span className="text-sm text-muted-foreground">{count.toLocaleString()}</span>
        </div>
        <div className="h-8 bg-muted rounded-lg overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-primary to-primary/70 rounded-lg transition-all duration-500"
            style={{ width: `${width}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-1">{rate}% conversion</p>
      </div>
      {!isLast && (
        <ArrowRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
      )}
    </div>
  );
}

// Main Dashboard Component
export default function AnalyticsDashboard() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [timeRange, setTimeRange] = useState(TIME_RANGES[0]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dashboard data states
  const [overview, setOverview] = useState(null);
  const [quickActions, setQuickActions] = useState([]);
  const [pageEngagement, setPageEngagement] = useState([]);
  const [agentUsage, setAgentUsage] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [trends, setTrends] = useState([]);
  const [alertSummary, setAlertSummary] = useState(null);
  const [testingAlert, setTestingAlert] = useState(false);
  
  // Fetch all dashboard data
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const hours = timeRange.hours;
      const days = Math.ceil(hours / 24);
      
      // Fetch all endpoints in parallel
      const [
        overviewRes,
        quickActionsRes,
        pageEngagementRes,
        agentUsageRes,
        funnelRes,
        trendsRes,
        alertsRes
      ] = await Promise.all([
        fetch(`${API_URL}/api/intelligence-core/analytics/dashboard/overview?hours=${hours}`),
        fetch(`${API_URL}/api/intelligence-core/analytics/quick-actions?hours=${hours}&limit=10`),
        fetch(`${API_URL}/api/intelligence-core/analytics/dashboard/page-engagement?hours=${hours}`),
        fetch(`${API_URL}/api/intelligence-core/analytics/dashboard/agent-usage?hours=${hours}`),
        fetch(`${API_URL}/api/intelligence-core/analytics/dashboard/funnel?hours=${hours}`),
        fetch(`${API_URL}/api/intelligence-core/analytics/dashboard/trends?days=${days}`),
        fetch(`${API_URL}/api/intelligence-core/alerts/summary`)
      ]);
      
      // Parse responses
      const overviewData = await overviewRes.json();
      const quickActionsData = await quickActionsRes.json();
      const pageEngagementData = await pageEngagementRes.json();
      const agentUsageData = await agentUsageRes.json();
      const funnelData = await funnelRes.json();
      const trendsData = await trendsRes.json();
      const alertsData = await alertsRes.json();
      
      setOverview(overviewData);
      setQuickActions(quickActionsData.quick_actions || []);
      setPageEngagement(pageEngagementData);
      setAgentUsage(agentUsageData);
      setFunnel(funnelData);
      setTrends(trendsData);
      setAlertSummary(alertsData);
      
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);
  
  // Trigger test alert
  const triggerTestAlert = async () => {
    setTestingAlert(true);
    try {
      const response = await fetch(`${API_URL}/api/intelligence-core/alerts/test?severity=info`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        alert('Test alert sent! Check logs for notification details.');
      }
    } catch (err) {
      console.error('Failed to trigger test alert:', err);
    } finally {
      setTestingAlert(false);
    }
  };
  
  // Fetch data on mount and time range change
  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardData();
    }
  }, [isAuthenticated, fetchDashboardData]);
  
  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }
  
  // Check for admin role - if user exists but is not admin
  if (user && user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="bg-white rounded-xl border border-border p-8 max-w-md text-center shadow-sm">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground mb-6">
            This dashboard requires administrator privileges. Contact your system administrator if you need access.
          </p>
          <Button onClick={() => window.history.back()} variant="outline">
            Go Back
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground font-['Merriweather']">
                Intelligence Assistant Analytics
              </h1>
              <p className="text-muted-foreground mt-1">
                Monitor assistant performance and user engagement
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Time Range Selector */}
              <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
                {TIME_RANGES.map((range) => (
                  <button
                    key={range.hours}
                    onClick={() => setTimeRange(range)}
                    className={cn(
                      "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                      timeRange.hours === range.hours
                        ? "bg-white text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
              
              {/* Refresh Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={fetchDashboardData}
                disabled={loading}
              >
                <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}
        
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-8">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <KPICard
                title="Total Interactions"
                value={overview?.total_interactions?.toLocaleString() || '0'}
                icon={Activity}
                color="primary"
                subtitle={`${overview?.unique_sessions || 0} unique sessions`}
              />
              <KPICard
                title="Quick Actions Clicked"
                value={overview?.total_quick_actions?.toLocaleString() || '0'}
                icon={Sparkles}
                color="primary"
              />
              <KPICard
                title="Successful Responses"
                value={overview?.successful_responses?.toLocaleString() || '0'}
                icon={CheckCircle}
                color="success"
              />
              <KPICard
                title="Avg Response Time"
                value={`${Math.round(overview?.avg_latency_ms || 0)}ms`}
                icon={Clock}
                color="warning"
              />
            </div>
            
            {/* Secondary KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <KPICard
                title="Messages Submitted"
                value={overview?.total_messages?.toLocaleString() || '0'}
                icon={MessageSquare}
                color="primary"
              />
              <KPICard
                title="Most Active Page"
                value={overview?.most_active_page || 'N/A'}
                icon={TrendingUp}
                color="primary"
              />
              <KPICard
                title="Most Active Agent"
                value={overview?.most_active_agent?.replace('_', ' ') || 'N/A'}
                icon={Bot}
                color="primary"
                subtitle="Handling most requests"
              />
            </div>
            
            {/* Alerts Summary Panel */}
            <div className="bg-card rounded-xl border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold text-foreground">Alerts & Monitoring</h2>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={triggerTestAlert}
                  disabled={testingAlert}
                >
                  <TestTube className={cn("w-4 h-4 mr-2", testingAlert && "animate-pulse")} />
                  Test Alert
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                {/* Active Alerts */}
                <div className={cn(
                  "rounded-lg p-4 border",
                  alertSummary?.active_count > 0 
                    ? "bg-red-50 border-red-200" 
                    : "bg-green-50 border-green-200"
                )}>
                  <div className="flex items-center gap-2 mb-2">
                    {alertSummary?.active_count > 0 ? (
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                    <span className="text-sm font-medium">Active Alerts</span>
                  </div>
                  <p className={cn(
                    "text-2xl font-bold",
                    alertSummary?.active_count > 0 ? "text-red-600" : "text-green-600"
                  )}>
                    {alertSummary?.active_count || 0}
                  </p>
                </div>
                
                {/* Critical */}
                <div className="rounded-lg p-4 border bg-muted/30 border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    <span className="text-sm font-medium">Critical</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    {alertSummary?.severity_breakdown?.critical || 0}
                  </p>
                </div>
                
                {/* Warning */}
                <div className="rounded-lg p-4 border bg-muted/30 border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    <span className="text-sm font-medium">Warning</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    {alertSummary?.severity_breakdown?.warning || 0}
                  </p>
                </div>
                
                {/* Today's Alerts */}
                <div className="rounded-lg p-4 border bg-muted/30 border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Info className="w-5 h-5 text-blue-500" />
                    <span className="text-sm font-medium">Today's Alerts</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    {alertSummary?.total_alerts_today || 0}
                  </p>
                </div>
              </div>
              
              {/* Active Alerts List */}
              {alertSummary?.active_alerts?.length > 0 ? (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-muted-foreground">Active Alerts</h3>
                  {alertSummary.active_alerts.map((alert, index) => (
                    <div 
                      key={index}
                      className={cn(
                        "rounded-lg p-4 border flex items-start gap-3",
                        alert.severity === 'critical' && "bg-red-50 border-red-200",
                        alert.severity === 'warning' && "bg-yellow-50 border-yellow-200",
                        alert.severity === 'info' && "bg-blue-50 border-blue-200"
                      )}
                    >
                      {alert.severity === 'critical' && <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />}
                      {alert.severity === 'warning' && <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />}
                      {alert.severity === 'info' && <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-foreground">{alert.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {alert.metric}: {alert.observed} (threshold: {alert.threshold})
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Triggered: {new Date(alert.triggered_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                  <p className="font-medium">All Systems Operational</p>
                  <p className="text-sm mt-1">No active alerts at this time</p>
                </div>
              )}
              
              {/* Recent Alerts */}
              {alertSummary?.recent_alerts?.length > 0 && (
                <div className="mt-6 pt-6 border-t border-border">
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">Recent Alerts</h3>
                  <div className="space-y-2">
                    {alertSummary.recent_alerts.slice(0, 5).map((alert, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between text-sm py-2 px-3 rounded bg-muted/30"
                      >
                        <div className="flex items-center gap-2">
                          {alert.severity === 'critical' && <AlertCircle className="w-4 h-4 text-red-500" />}
                          {alert.severity === 'warning' && <AlertTriangle className="w-4 h-4 text-yellow-500" />}
                          {alert.severity === 'info' && <Info className="w-4 h-4 text-blue-500" />}
                          <span className="text-foreground">{alert.title}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            "text-xs px-2 py-0.5 rounded",
                            alert.status === 'resolved' ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                          )}>
                            {alert.status}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(alert.triggered_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Charts Row 1: Quick Actions & Page Engagement */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Quick Actions */}
              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center gap-2 mb-6">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold text-foreground">Top Quick Actions</h2>
                </div>
                
                {quickActions.length > 0 ? (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={quickActions.slice(0, 5)}
                        layout="vertical"
                        margin={{ top: 0, right: 30, left: 100, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis type="number" tick={{ fontSize: 12 }} />
                        <YAxis 
                          type="category" 
                          dataKey="quick_action_label" 
                          tick={{ fontSize: 11 }}
                          width={95}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar 
                          dataKey="click_count" 
                          fill={COLORS.primary} 
                          radius={[0, 4, 4, 0]}
                          name="Clicks"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No quick action data available
                  </div>
                )}
              </div>
              
              {/* Page Engagement */}
              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center gap-2 mb-6">
                  <PieChartIcon className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold text-foreground">Page Engagement</h2>
                </div>
                
                {pageEngagement.length > 0 ? (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pageEngagement}
                          dataKey="event_count"
                          nameKey="page_context"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ page_context, percent }) => 
                            `${page_context.substring(0, 12)}${page_context.length > 12 ? '...' : ''} (${(percent * 100).toFixed(0)}%)`
                          }
                          labelLine={false}
                        >
                          {pageEngagement.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={COLORS.chart[index % COLORS.chart.length]} 
                            />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No page engagement data available
                  </div>
                )}
              </div>
            </div>
            
            {/* Charts Row 2: Agent Usage & Funnel */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Agent Usage */}
              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Bot className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold text-foreground">Agent Usage Distribution</h2>
                </div>
                
                {agentUsage.length > 0 ? (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={agentUsage}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis 
                          dataKey="agent" 
                          tick={{ fontSize: 10, angle: -45, textAnchor: 'end' }}
                          height={60}
                        />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px'
                          }}
                          formatter={(value, name) => [
                            name === 'request_count' ? `${value} requests` : `${value}%`,
                            name === 'request_count' ? 'Requests' : 'Percentage'
                          ]}
                        />
                        <Bar 
                          dataKey="request_count" 
                          fill={COLORS.primary} 
                          radius={[4, 4, 0, 0]}
                          name="request_count"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No agent usage data available
                  </div>
                )}
              </div>
              
              {/* Interaction Funnel */}
              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center gap-2 mb-6">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold text-foreground">User Journey Funnel</h2>
                </div>
                
                {funnel.length > 0 ? (
                  <div className="space-y-4">
                    {funnel.map((step, index) => (
                      <FunnelStep
                        key={step.step}
                        step={step.step}
                        count={step.count}
                        rate={step.conversion_rate}
                        isLast={index === funnel.length - 1}
                        maxCount={funnel[0]?.count || 1}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No funnel data available
                  </div>
                )}
              </div>
            </div>
            
            {/* Trends Chart */}
            <div className="bg-card rounded-xl border border-border p-6">
              <div className="flex items-center gap-2 mb-6">
                <Activity className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-foreground">Engagement Trends</h2>
              </div>
              
              {trends.length > 0 ? (
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={trends}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(date) => {
                          const d = new Date(date);
                          return `${d.getMonth() + 1}/${d.getDate()}`;
                        }}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px'
                        }}
                        labelFormatter={(date) => new Date(date).toLocaleDateString()}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="events" 
                        stroke={COLORS.primary} 
                        strokeWidth={2}
                        dot={{ fill: COLORS.primary, strokeWidth: 2 }}
                        name="Total Events"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="quick_actions" 
                        stroke={COLORS.chart[1]} 
                        strokeWidth={2}
                        dot={{ fill: COLORS.chart[1], strokeWidth: 2 }}
                        name="Quick Actions"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="responses" 
                        stroke={COLORS.success} 
                        strokeWidth={2}
                        dot={{ fill: COLORS.success, strokeWidth: 2 }}
                        name="Responses"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[350px] flex items-center justify-center text-muted-foreground">
                  No trend data available
                </div>
              )}
            </div>
            
            {/* Quick Actions Table */}
            {quickActions.length > 0 && (
              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold text-foreground">Quick Actions Details</h2>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Quick Action</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Page Context</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Clicks</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Unique Sessions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quickActions.map((action, index) => (
                        <tr key={index} className="border-b border-border/50 hover:bg-muted/30">
                          <td className="py-3 px-4 text-sm text-foreground">{action.quick_action_label}</td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">{action.page_context || 'N/A'}</td>
                          <td className="py-3 px-4 text-sm text-foreground text-right font-medium">{action.click_count}</td>
                          <td className="py-3 px-4 text-sm text-muted-foreground text-right">{action.unique_sessions}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
